// social-accounts-tab.tsx
import { observer } from 'mobx-react-lite';
import SocialAccountSelect from '../social-accounts/social-account';
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import {
  Card,
  Typography,
  Button,
  Empty,
  Row,
  Col,
  Tag,
  Avatar,
  Flex,
  Space,
  Divider,
} from 'antd';
import { useStore } from '../../helpers/use-store';
import {
  PlusOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CrownOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import SocialIcon from '../../components/social-provider-icon/social-provider-icon';
import { SocialAccount, SocialProvider } from '@monorepo/types';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { getEnv } from '../../helpers/mobx-easy-wrapper';
import {
  SocialAccountsModal,
  SocialAccountsModalRef,
} from '../social-accounts/social-accounts-modal';

import './social-accounst-tab.scss';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const SocialAccountsTab = () => {
  const { t } = useTranslation();
  const {
    envConfig: { youtube_client_id },
  } = getEnv();

  const {
    dataStore: { socialAccountStore, userStore },
  } = useStore();

  useEffect(() => {
    socialAccountStore.fetchAll();
  }, []);

  const navigate = useNavigate();

  const modalRef = useRef<SocialAccountsModalRef>(null);

  const handleAddAccount = () => {
    modalRef.current?.open();
  };

  const handleAccountAdded = () => {
    socialAccountStore.fetchAll();
    modalRef.current?.close();
  };

  const handleDisconnect = async (accountId: string) => {
    // Add your disconnect logic here
    await socialAccountStore.delete(accountId);
  };

  const handleReconnect = (account: SocialAccount) => {
    // Open modal to reconnect
    modalRef.current?.open();
  };

  const connectedAccounts = socialAccountStore.items;

  const getProviderColor = (provider: SocialProvider) => {
    switch (provider) {
      case SocialProvider.TikTok:
        return '#000000';
      case SocialProvider.YouTube:
        return '#ff0000';
      case SocialProvider.Instagram:
        return '#e4405f';
      default:
        return '#1890ff';
    }
  };

  const renderAccountCard = (account: SocialAccount) => {
    const isInvalid = account.tokenInvalid;

    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={account._id}>
        <Card
          className={`social-account-card ${isInvalid ? 'invalid' : ''}`}
          hoverable={!isInvalid}
        >
          {/* Status Badge */}
          <div className="status-badge">
            {isInvalid ? (
              <Tag icon={<WarningOutlined />} color="error">
                {t('socialAccounts.needsReconnection')}
              </Tag>
            ) : (
              <Tag icon={<CheckCircleOutlined />} color="success">
                {t('socialAccounts.account.connected')}
              </Tag>
            )}
          </div>

          {/* Account Info */}
          <div className="account-header">
            <Avatar
              size={64}
              src={account.avatarUrl}
              className="account-avatar"
            />

            <Title
              level={5}
              style={{ margin: '12px 0 4px 0', textAlign: 'center' }}
            >
              {account.title}
            </Title>

            <Tag
              style={{
                margin: '4px 0 0 0',
                padding: '4px 12px',
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: '12px',
                border: 'none',
                background: 'var(--ant-color-fill-quaternary)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span
                style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}
              >
                <SocialIcon provider={account.provider} />
              </span>
              <span style={{ lineHeight: 1 }}>{account.provider}</span>
            </Tag>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          {/* Account Stats/Info */}
          <div className="account-info">
            <Flex justify="space-around" gap={8}>
              <div className="info-item">
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {t('socialAccounts.username')}
                </Text>
                <Text strong style={{ fontSize: '13px' }}>
                  @{account.title || 'N/A'}
                </Text>
              </div>
            </Flex>
          </div>

          {/* Actions */}
          <div className="account-actions">
            {isInvalid ? (
              <Button
                type="primary"
                block
                icon={<ReloadOutlined />}
                onClick={() => handleReconnect(account)}
              >
                {t('socialAccounts.reconnect')}
              </Button>
            ) : null}
            {/*(*/}
            {/*<Button*/}
            {/*  danger*/}
            {/*  block*/}
            {/*  icon={<DeleteOutlined />}*/}
            {/*  onClick={() => handleDisconnect(account._id)}*/}
            {/*>*/}
            {/*  Disconnect*/}
            {/*</Button>*/}
            {/*)}*/}
          </div>
        </Card>
      </Col>
    );
  };

  return (
    <div className="social-accounts-page">
      <Helmet>
        <title>Social Accounts | ShortsNinja</title>
        <meta
          name="description"
          content="Connect and manage your social media accounts. Automate your faceless video distribution."
        />
        <meta
          name="keywords"
          content="social accounts, account connection, ShortsNinja, faceless videos, ai video automation"
        />
      </Helmet>

      {/* Header Section */}
      <div className="page-header">
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              {t('socialAccounts.title')}
            </Title>
            <Text type="secondary">{t('socialAccounts.tabSubtitle')}</Text>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={handleAddAccount}
            disabled={!userStore.isSubscriptionActive}
          >
            {t('socialAccounts.addAccount')}
          </Button>
        </Flex>
      </div>

      {/* Connected Accounts Grid */}
      {connectedAccounts.length > 0 ? (
        <Row gutter={[24, 24]}>{connectedAccounts.map(renderAccountCard)}</Row>
      ) : (
        <Card className="empty-state-card">
          {!userStore.isSubscriptionActive ? (
            <div className="upgrade-prompt">
              <div className="upgrade-icon">
                <CrownOutlined />
              </div>
              <Title level={3} style={{ margin: '16px 0 8px 0' }}>
                {t('socialAccounts.unlockTitle')}
              </Title>
              <Text
                type="secondary"
                style={{
                  fontSize: '15px',
                  marginBottom: '24px',
                  display: 'block',
                }}
              >
                {t('socialAccounts.unlockSubtitle')}
              </Text>

              {/* Feature highlights */}
              <div className="upgrade-features">
                <Space direction="vertical" size="small" align="start">
                  <Flex align="center" gap={8}>
                    <CheckCircleOutlined
                      style={{ color: '#52c41a', fontSize: '16px' }}
                    />
                    <Text>{t('socialAccounts.feature1')}</Text>
                  </Flex>
                  <Flex align="center" gap={8}>
                    <CheckCircleOutlined
                      style={{ color: '#52c41a', fontSize: '16px' }}
                    />
                    <Text>{t('socialAccounts.feature2')}</Text>
                  </Flex>
                  <Flex align="center" gap={8}>
                    <CheckCircleOutlined
                      style={{ color: '#52c41a', fontSize: '16px' }}
                    />
                    <Text>{t('socialAccounts.feature3')}</Text>
                  </Flex>
                </Space>
              </div>

              <Button
                type="primary"
                size="large"
                icon={<RocketOutlined />}
                onClick={() => navigate('/credits')}
                style={{
                  marginTop: '24px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                {t('socialAccounts.viewPlans')}
              </Button>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical" size="middle">
                  <Text>{t('socialAccounts.noAccountsYet')}</Text>
                  <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={handleAddAccount}
                  >
                    {t('socialAccounts.connectFirst')}
                  </Button>
                </Space>
              }
            />
          )}
        </Card>
      )}

      {/* Modal */}
      <GoogleOAuthProvider clientId={youtube_client_id}>
        <SocialAccountsModal
          ref={modalRef}
          onAccountAdded={handleAccountAdded}
        />
      </GoogleOAuthProvider>
    </div>
  );
};

export default observer(SocialAccountsTab);
